import React from "react";
import { Select } from "antd";
import { AGENT_HAS_CONTAINER_FILTER } from "../../config/agentPortal";
import { AgentContainerOption } from "../../services/agentPortalService";
import {
  AGENT_PICKUP_DATE_FILTERS,
  AgentPickupDateFilter,
} from "../../utils/agentBookingPickupDate";
import {
  AGENT_FILTER_SELECT_PROPS,
  AgentFilterToolbar,
} from "./AgentFilterToolbar";
import {
  SERVICE_LEVEL_LABELS,
  SERVICE_LEVEL_VALUES,
  type ServiceLevel,
} from "../../utils/serviceLevelUtils";

export type AgentHasContainerFilter = "yes" | "no";

interface AgentBookingsFiltersProps {
  search: string;
  companyId?: number;
  transportTypeId?: number;
  hasContainer?: AgentHasContainerFilter;
  containerId?: number;
  masterContainerId?: number;
  bookingTypeId?: number;
  serviceLevel?: ServiceLevel;
  pickupFilter?: AgentPickupDateFilter;
  companies: { id: number; name: string }[];
  transportTypes: { id: number; name: string }[];
  bookingTypes: { id: number; name: string }[];
  containers: AgentContainerOption[];
  masters: { id: number; reference: string | null }[];
  loadingOptions: boolean;
  hasActiveFilters: boolean;
  resultCount: number;
  statusFilter: string;
  searchMode: "none" | "id";
  listTruncated?: boolean;
  pickupSortActive?: boolean;
  onSearchChange: (value: string) => void;
  onCompanyChange: (value?: number) => void;
  onTransportChange: (value?: number) => void;
  onHasContainerChange: (value?: AgentHasContainerFilter) => void;
  onContainerChange: (value?: number) => void;
  onMasterContainerChange: (value?: number) => void;
  onBookingTypeChange: (value?: number) => void;
  onServiceLevelChange: (value?: ServiceLevel) => void;
  onPickupFilterChange: (value?: AgentPickupDateFilter) => void;
  onClearFilters: () => void;
  onRefresh: () => void;
}

const formatContainerLabel = (container: AgentContainerOption) => {
  const transport = Array.isArray(container.transport_type_info)
    ? container.transport_type_info[0]?.name
    : container.transport_type_info?.name;
  const ref = container.reference || `Container ${container.id}`;
  const status = container.status ? ` · ${container.status}` : "";
  const type = transport ? ` · ${transport}` : "";
  return `${ref}${type}${status}`;
};

const buildResultHint = (props: AgentBookingsFiltersProps): string | undefined => {
  const parts: string[] = [];
  if (props.searchMode === "id") {
    parts.push("ID lookup");
  } else if (props.statusFilter !== "all") {
    parts.push(props.statusFilter.replace(/_/g, " "));
  } else if (props.listTruncated) {
    parts.push("latest 1000");
  }

  const pickupLabel = AGENT_PICKUP_DATE_FILTERS.find(
    (f) => f.value === props.pickupFilter
  )?.label;
  if (pickupLabel) parts.push(pickupLabel);
  if (props.pickupSortActive) parts.push("sorted by urgency");

  return parts.length > 0 ? parts.join(" · ") : undefined;
};

export const AgentBookingsFilters: React.FC<AgentBookingsFiltersProps> = (
  props
) => {
  const {
    search,
    companyId,
    transportTypeId,
    hasContainer,
    containerId,
    masterContainerId,
    bookingTypeId,
    serviceLevel,
    pickupFilter,
    companies,
    transportTypes,
    bookingTypes,
    containers,
    masters,
    loadingOptions,
    hasActiveFilters,
    resultCount,
    onSearchChange,
    onCompanyChange,
    onTransportChange,
    onHasContainerChange,
    onContainerChange,
    onMasterContainerChange,
    onBookingTypeChange,
    onServiceLevelChange,
    onPickupFilterChange,
    onClearFilters,
    onRefresh,
  } = props;

  return (
    <AgentFilterToolbar
      search={search}
      searchPlaceholder="Search booking (EFF…), ID, company…"
      onSearchChange={onSearchChange}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={onClearFilters}
      onRefresh={onRefresh}
      resultCount={resultCount}
      resultHint={buildResultHint(props)}
    >
      <Select
        {...AGENT_FILTER_SELECT_PROPS}
        showSearch
        optionFilterProp="label"
        placeholder="Company"
        value={companyId}
        onChange={onCompanyChange}
        loading={loadingOptions}
        options={companies.map((c) => ({ value: c.id, label: c.name }))}
      />
      <Select
        {...AGENT_FILTER_SELECT_PROPS}
        placeholder="Transport"
        value={transportTypeId}
        onChange={onTransportChange}
        loading={loadingOptions}
        options={transportTypes.map((t) => ({ value: t.id, label: t.name }))}
      />
      <Select
        {...AGENT_FILTER_SELECT_PROPS}
        placeholder="LCL / FCL"
        value={bookingTypeId}
        onChange={onBookingTypeChange}
        loading={loadingOptions}
        options={bookingTypes.map((t) => ({ value: t.id, label: t.name }))}
      />
      <Select
        {...AGENT_FILTER_SELECT_PROPS}
        placeholder="Service level"
        value={serviceLevel}
        onChange={onServiceLevelChange}
        options={SERVICE_LEVEL_VALUES.map((value) => ({
          value,
          label: SERVICE_LEVEL_LABELS[value],
        }))}
      />
      <div style={{ gridColumn: "span 2" }}>
        <Select
          {...AGENT_FILTER_SELECT_PROPS}
          placeholder="Goods ready"
          value={pickupFilter}
          onChange={onPickupFilterChange}
          dropdownMatchSelectWidth={false}
          popupMatchSelectWidth={320}
          options={AGENT_PICKUP_DATE_FILTERS.map((o) => ({
            value: o.value,
            label: o.label,
          }))}
        />
      </div>
      <Select
        {...AGENT_FILTER_SELECT_PROPS}
        placeholder="Has container"
        value={hasContainer}
        onChange={onHasContainerChange}
        disabled={containerId != null}
        options={AGENT_HAS_CONTAINER_FILTER.map((o) => ({
          value: o.value,
          label: o.label,
        }))}
      />
      <Select
        {...AGENT_FILTER_SELECT_PROPS}
        showSearch
        optionFilterProp="label"
        placeholder="Consolidation"
        value={containerId}
        onChange={onContainerChange}
        loading={loadingOptions}
        disabled={hasContainer === "no"}
        options={containers.map((c) => ({
          value: c.id,
          label: formatContainerLabel(c),
        }))}
      />
      <Select
        {...AGENT_FILTER_SELECT_PROPS}
        showSearch
        optionFilterProp="label"
        placeholder="Master BL/AWB"
        value={masterContainerId}
        onChange={onMasterContainerChange}
        loading={loadingOptions}
        options={masters.map((m) => ({
          value: m.id,
          label: m.reference || `Master #${m.id}`,
        }))}
      />
    </AgentFilterToolbar>
  );
};
